import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const inputClass = 'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500';
const todayString = () => new Date().toISOString().split('T')[0];

const HiredStatusModal = ({ isOpen, applicant, positionOptions = [], saving = false, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    position: '',
    hireDate: '',
    startDate: '',
    salary: '',
    email: ''
  });
  const [error, setError] = useState('');

  const uniquePositionOptions = useMemo(() => {
    const options = [...new Set(positionOptions.filter(Boolean).map((option) => String(option).trim()).filter(Boolean))];
    if (formData.position && !options.includes(formData.position)) {
      options.unshift(formData.position);
    }
    return options;
  }, [positionOptions, formData.position]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      position: applicant?.position || '',
      hireDate: applicant?.hireDate ? applicant.hireDate.split('T')[0] : todayString(),
      startDate: applicant?.startDate ? applicant.startDate.split('T')[0] : '',
      salary: applicant?.salary ? String(applicant.salary) : '',
      email: applicant?.email || ''
    });
    setError('');
  }, [isOpen, applicant]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const numericSalary = Number(formData.salary);
    if (!formData.email.trim()) {
      setError('Email is required before marking this lead as hired.');
      return;
    }
    if (!formData.position.trim()) {
      setError('Position is required before marking this lead as hired.');
      return;
    }
    if (!formData.hireDate) {
      setError('Hire date is required.');
      return;
    }
    if (!formData.startDate) {
      setError('Start date is required.');
      return;
    }
    if (!Number.isFinite(numericSalary) || numericSalary < 10000 || numericSalary > 100000) {
      setError('Salary must be a number between 10000 and 100000.');
      return;
    }

    setError('');
    onSave({
      position: formData.position.trim(),
      hireDate: formData.hireDate,
      startDate: formData.startDate,
      salary: numericSalary,
      email: formData.email.trim()
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Confirm Hire Details</h3>
            <p className="mt-1 text-sm text-gray-500">Complete these fields before changing the lead status to hired.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 transition hover:text-gray-600" disabled={saving}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700">Position <span className="text-red-500">*</span></label>
            <select name="position" value={formData.position} onChange={handleChange} className={inputClass} required>
              <option value="">Select a position</option>
              {uniquePositionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Hire Date <span className="text-red-500">*</span></label>
              <input type="date" name="hireDate" value={formData.hireDate} onChange={handleChange} className={inputClass} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date <span className="text-red-500">*</span></label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className={inputClass} required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Salary <span className="text-red-500">*</span></label>
            <input
              type="number"
              name="salary"
              min="10000"
              max="100000"
              step="1"
              value={formData.salary}
              onChange={handleChange}
              className={`${inputClass} bg-black text-white`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} required />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save and Mark Hired'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HiredStatusModal;
